import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney, formatApiErrorDetail, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet } from "lucide-react";

const FILTERS = [
  { key: "awaiting", label: "Awaiting Payment" },
  { key: "scheduled", label: "Scheduled" },
  { key: "processing", label: "Processing" },
  { key: "paid", label: "Paid" },
  { key: "failed", label: "Failed" },
];

const STATUS_STYLE = {
  awaiting: "bg-slate-100 text-slate-600",
  scheduled: "bg-sky-100 text-sky-700",
  processing: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
};

const optionLabel = (t) => (t === "expedited" ? "24-Hour" : t ? "Standard" : "—");
const dt = (v) => (v ? new Date(v).toLocaleString() : "—");

export default function AdminPayouts() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState(sp.get("filter") || "scheduled");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailAudit, setDetailAudit] = useState([]);
  const [settle, setSettle] = useState(null); // {row, result}
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [gps, setGps] = useState(null);
  const [ovComments, setOvComments] = useState("");
  const [ovBusy, setOvBusy] = useState(false);
  const [locReason, setLocReason] = useState("");
  const [locBusy, setLocBusy] = useState(false);
  const [safetyNet, setSafetyNet] = useState(null);
  useEffect(() => {
    api.get("/admin/payout-safety-net").then(({ data }) => setSafetyNet(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: transfers }, { data: comps }] = await Promise.all([
        api.get("/admin/payout-transfers"),
        api.get("/compensations"),
      ]);
      const transferComps = new Set(transfers.map((t) => t.compensation_id));
      const awaiting = (comps || [])
        .filter((c) => c.payout_eligible && !transferComps.has(c.id) && c.payment_status !== "paid")
        .map((c) => ({
          id: null, compensation_id: c.id, order_id: c.order_id, job_number: c.job_number,
          server_name: c.server_name, server_id: c.server_id, currency: c.currency,
          gross: c.net_payout, fee: 0, net_amount: c.net_payout, method: null,
          transfer_type: null, status: "awaiting", reference: null, created_at: c.created_at,
        }));
      setRows([...transfers, ...awaiting]);
    } catch (e) {
      toast.error("Could not load payouts");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = FILTERS.reduce((m, f) => { m[f.key] = rows.filter((r) => r.status === f.key).length; return m; }, {});
  const filtered = rows.filter((r) => r.status === filter);

  const openDetail = async (row) => {
    setDetail(row); setDetailAudit([]); setGps(null); setOvComments(""); setLocReason("");
    if (row.compensation_id) {
      try { const { data } = await api.get(`/compensations/${row.compensation_id}/gps-status`); setGps(data); } catch {}
    }
    if (row.order_id) {
      try {
        const { data } = await api.get(`/orders/${row.order_id}`);
        setDetailAudit((data.audit_log || []).filter((e) => /^(transfer_|payout_|compensation_)/.test(e.event || "")));
      } catch {}
    }
  };

  const approveOverride = async () => {
    if (!detail?.compensation_id) return;
    if (!ovComments.trim()) { toast.error("Enter review/comments to approve the override."); return; }
    setOvBusy(true);
    try {
      const { data } = await api.post(`/compensations/${detail.compensation_id}/gps-override`, { comments: ovComments.trim() });
      toast.success(`GPS override ${data.override_number} approved — payout now eligible.`);
      const { data: g } = await api.get(`/compensations/${detail.compensation_id}/gps-status`);
      setGps(g); setOvComments(""); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Could not approve override"); }
    finally { setOvBusy(false); }
  };

  const approveLocationException = async () => {
    if (!detail?.compensation_id) return;
    if (!locReason.trim()) { toast.error("Enter a reason to approve the location exception."); return; }
    setLocBusy(true);
    try {
      const { data } = await api.post(`/compensations/${detail.compensation_id}/location-exception`, { reason: locReason.trim() });
      toast.success(`Location exception ${data.override_number} approved — payout now eligible.`);
      const { data: g } = await api.get(`/compensations/${detail.compensation_id}/gps-status`);
      setGps(g); setLocReason(""); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Could not approve location exception"); }
    finally { setLocBusy(false); }
  };

  const doSettle = async () => {
    if (!settle) return;
    setBusy(true);
    try {
      await api.post(`/admin/payout-transfers/${settle.row.id}/settle`, {
        result: settle.result, reference: ref || undefined, note: note || undefined,
      });
      toast.success(settle.result === "paid" ? "Payout marked Paid — server notified" : "Payout marked Failed");
      setSettle(null); setRef(""); setNote(""); setDetail(null); await load();
    } catch (e) { toast.error(extractErrorMessage(e, "Could not settle")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-payouts-page">
      <div className="flex items-center gap-2">
        <Wallet className="h-6 w-6 text-emerald-600" />
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Payouts</h2>
          <p className="text-slate-500">Administer server payout transfers and settlement.</p>
        </div>
      </div>

      {safetyNet && safetyNet.rows.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="safety-net-report">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-amber-900">Safety-net watch — client invoice unpaid past 30 days</h3>
              <p className="max-w-2xl text-xs text-amber-700">Read-only. All payout conditions met except client payment. §19 interest (1.5%/mo) is owed by ARPS to the provider on unpaid compensation from the funding-due date. Nothing is charged or released here.</p>
            </div>
            <div className="text-right text-xs text-amber-800">
              <div><b data-testid="safety-net-count">{safetyNet.totals.count}</b> job(s) · <b>{safetyNet.totals.overdue}</b> overdue</div>
              <div>Comp {fmtMoney("USD", safetyNet.totals.comp_total)} · Interest {fmtMoney("USD", safetyNet.totals.interest_total)}</div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-amber-100 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-amber-50 text-left uppercase tracking-wide text-amber-700">
                <tr>
                  <th className="px-3 py-2">Job #</th><th className="px-3 py-2">Provider</th><th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Comp</th><th className="px-3 py-2">Conditions met</th><th className="px-3 py-2">30-day date</th>
                  <th className="px-3 py-2">Fund by (10 bd)</th><th className="px-3 py-2">Days out</th><th className="px-3 py-2">Interest</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-50">
                {safetyNet.rows.map((r) => (
                  <tr key={r.compensation_id} data-testid={`safety-net-row-${r.job_number}`} className={r.overdue ? "bg-amber-50/40" : ""}>
                    <td className="px-3 py-2">
                      <button onClick={() => r.order_id && nav(`/admin/jobs/${r.order_id}`)} className="font-mono-arps font-semibold text-emerald-700 hover:underline">#{r.job_number}</button>
                    </td>
                    <td className="px-3 py-2">{r.provider_name || "—"}</td>
                    <td className="px-3 py-2">{r.client_name || "—"}</td>
                    <td className="px-3 py-2 font-semibold">{fmtMoney(r.currency, r.comp_amount)}</td>
                    <td className="px-3 py-2 text-slate-500">{dt(r.conditions_met_at)}</td>
                    <td className="px-3 py-2 text-slate-500">{dt(r.safety_net_date)}</td>
                    <td className="px-3 py-2 text-slate-500">{dt(r.funding_due_date)}</td>
                    <td className="px-3 py-2 font-semibold">{r.days_outstanding}{r.overdue ? " ⚠" : ""}</td>
                    <td className="px-3 py-2">{r.accrued_interest > 0 ? fmtMoney(r.currency, r.accrued_interest) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${filter === f.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {f.label} <span className="ml-1 text-xs opacity-70">{counts[f.key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Job #</th><th className="px-4 py-3">Server</th>
              <th className="px-4 py-3">Compensation</th><th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Option</th><th className="px-4 py-3">Fee</th>
              <th className="px-4 py-3">Net</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Initiated</th><th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400" data-testid="no-payouts">No payouts in this status.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={(r.id || r.compensation_id) + i} data-testid={`payout-row-${r.job_number}`} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <button data-testid={`row-job-link-${r.job_number}`} onClick={() => r.order_id && nav(`/admin/jobs/${r.order_id}`)}
                    className="font-mono-arps font-semibold text-emerald-700 hover:underline">#{r.job_number}</button>
                </td>
                <td className="px-4 py-3">
                  <button data-testid={`row-server-link-${r.job_number}`} onClick={() => r.server_id && nav(`/admin/servers/${r.server_id}`)}
                    className="text-slate-700 hover:underline">{r.server_name || "—"}</button>
                </td>
                <td className="px-4 py-3">{fmtMoney(r.currency, r.gross)}</td>
                <td className="px-4 py-3">{r.method ? `${r.method.replace("_", " ")}${r.masked_ref ? " · " + r.masked_ref : ""}` : "—"}</td>
                <td className="px-4 py-3">{optionLabel(r.transfer_type)}</td>
                <td className="px-4 py-3">{fmtMoney(r.currency, r.fee || 0)}</td>
                <td className="px-4 py-3 font-semibold">{fmtMoney(r.currency, r.net_amount)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-slate-500">{dt(r.created_at)}</td>
                <td className="px-4 py-3 font-mono-arps text-xs text-slate-500">{r.reference || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" data-testid={`detail-${r.job_number}`} onClick={() => openDetail(r)}>View</Button>
                    {(r.status === "scheduled" || r.status === "processing") && r.id && (
                      <>
                        <Button size="sm" data-testid={`mark-paid-${r.id}`} onClick={() => { setRef(""); setNote(""); setSettle({ row: r, result: "paid" }); }} className="bg-emerald-600 text-white hover:bg-emerald-700">Mark Paid</Button>
                        <Button size="sm" variant="outline" data-testid={`mark-failed-${r.id}`} onClick={() => { setRef(""); setNote(""); setSettle({ row: r, result: "failed" }); }} className="border-red-300 text-red-600 hover:bg-red-50">Mark Failed</Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="payout-detail-modal">
          <DialogHeader><DialogTitle>Payout detail — #{detail?.job_number}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
                <Kv k="Server" v={detail.server_name} />
                <Kv k="Compensation" v={fmtMoney(detail.currency, detail.gross)} />
                <Kv k="Payout method" v={detail.method ? `${detail.method.replace("_", " ")}${detail.masked_ref ? " · " + detail.masked_ref : ""}` : "—"} />
                <Kv k="Option" v={optionLabel(detail.transfer_type)} />
                <Kv k="Expedited fee" v={fmtMoney(detail.currency, detail.fee || 0)} />
                <Kv k="Net payout" v={fmtMoney(detail.currency, detail.net_amount)} />
                <Kv k="Status" v={detail.status} />
                <Kv k="Reference" v={detail.reference || "—"} />
                <Kv k="Initiated" v={dt(detail.created_at)} />
                <Kv k="Paid at" v={dt(detail.paid_at)} />
                {detail.settlement_ref && <Kv k="Settlement ref" v={detail.settlement_ref} />}
                {detail.settled_by && <Kv k="Settled by" v={detail.settled_by} />}
                {detail.failed_at && <Kv k="Failed at" v={dt(detail.failed_at)} />}
                {detail.failure_note && <Kv k="Failure note" v={detail.failure_note} />}
              </dl>
              {detailAudit.length > 0 && (
                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Audit events</p>
                  <ul className="space-y-1" data-testid="detail-audit">
                    {detailAudit.map((e, i) => (
                      <li key={i} className="text-xs text-slate-600"><span className="font-mono-arps text-slate-400">{dt(e.at)}</span> — {e.detail}</li>
                    ))}
                  </ul>
                </div>
              )}
              {gps?.outside_service_location && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="gps-override-panel">
                  <p className="text-sm font-bold text-amber-800">Outside Service Location</p>
                  <p className="mt-1 text-xs text-amber-800">GPS distance: <b>{gps.distance_m != null ? `${Math.round(gps.distance_m)} m` : "—"}</b> · Allowed: <b>{gps.allowed_m != null ? `${Math.round(gps.allowed_m)} m` : "—"}</b></p>
                  <p className="mt-1 text-xs text-amber-800">Payout: <b>{gps.override_approved ? "OVERRIDE APPROVED" : "BLOCKED"}</b> · Required service-location evidence: <b>{gps.evidence_satisfied ? "Satisfied" : "Not Satisfied"}</b></p>
                  <p className="mt-1 text-xs text-amber-700">Approved overrides this year: {gps.overrides_used_this_year}/{gps.override_limit}{gps.override_number ? ` · this assignment: ${gps.override_number}/${gps.override_limit}` : ""}</p>
                  {gps.limit_reached && !gps.override_approved && (
                    <p className="mt-2 rounded bg-red-100 px-2 py-1 text-xs font-bold text-red-700" data-testid="gps-limit-reached">Annual GPS Override Limit Reached — {gps.override_limit}/{gps.override_limit}</p>
                  )}
                  {!gps.override_approved && !gps.limit_reached && gps.evidence_satisfied && (
                    <div className="mt-3 space-y-2">
                      <Label className="text-xs">Admin review / comments (required)</Label>
                      <Input data-testid="gps-override-comments" value={ovComments} onChange={(e) => setOvComments(e.target.value)} placeholder="Why is this exception approved?" />
                      <Button size="sm" data-testid="gps-override-approve" disabled={ovBusy} onClick={approveOverride} className="bg-amber-600 text-white hover:bg-amber-700">
                        {ovBusy ? "Approving…" : "Approve Override"}
                      </Button>
                    </div>
                  )}
                  {!gps.override_approved && !gps.evidence_satisfied && (
                    <div className="mt-2 space-y-2" data-testid="location-exception-panel">
                      <p className="text-xs text-amber-700">Required service-location evidence can't be satisfied (e.g. no photo required and the address couldn't be geocoded). An admin may approve a one-time location exception to release this payout.</p>
                      {!gps.limit_reached && (
                        <>
                          <Label className="text-xs">Reason (required)</Label>
                          <Input data-testid="location-exception-reason" value={locReason} onChange={(e) => setLocReason(e.target.value)} placeholder="Why is this location exception approved?" />
                          <Button size="sm" data-testid="location-exception-approve" disabled={locBusy} onClick={approveLocationException} className="bg-amber-600 text-white hover:bg-amber-700">
                            {locBusy ? "Approving…" : "Approve location exception"}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  {gps.census_verification && (
                    <div className="mt-3 border-t border-amber-200 pt-2" data-testid="payout-census-verify">
                      <p className="text-xs font-bold uppercase tracking-wide text-amber-700">ARPS Verify (US Census Bureau Geocoder)</p>
                      <p className="mt-1 text-xs text-amber-800">Result: <b data-testid="payout-census-result">{gps.census_verification.result || "—"}</b></p>
                      <p className="mt-0.5 text-xs text-amber-800">Observed: {gps.census_verification.observed_address || "Insufficient Data"}</p>
                      <p className="text-xs text-amber-800">Target: {gps.census_verification.target_address || "—"}</p>
                      {gps.census_verification.disclaimer && <p className="mt-1 text-[10px] italic leading-tight text-amber-600">{gps.census_verification.disclaimer}</p>}
                    </div>
                  )}
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => detail.order_id && nav(`/admin/jobs/${detail.order_id}`)}>Open job #{detail.job_number}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settle confirm */}
      <Dialog open={!!settle} onOpenChange={(o) => !o && setSettle(null)}>
        <DialogContent className="max-w-md" data-testid="settle-dialog">
          <DialogHeader><DialogTitle>{settle?.result === "paid" ? "Mark payout Paid" : "Mark payout Failed"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">#{settle?.row?.job_number} — {fmtMoney(settle?.row?.currency, settle?.row?.net_amount)} to {settle?.row?.server_name}.</p>
            {settle?.result === "paid" ? (
              <div><Label>Settlement reference (optional)</Label><Input data-testid="settle-reference" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Bank/processor reference" /></div>
            ) : (
              <div><Label>Failure note (optional)</Label><Input data-testid="settle-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for failure" /></div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="settle-confirm" onClick={doSettle} disabled={busy}
              className={settle?.result === "paid" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-600 text-white hover:bg-red-700"}>
              {busy ? "Saving…" : settle?.result === "paid" ? "Confirm Paid" : "Confirm Failed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ k, v }) {
  return (<><dt className="text-slate-500">{k}</dt><dd className="text-right font-semibold text-slate-900">{v}</dd></>);
}
